import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { Spinner, EmptyMsg } from '../WalletPagePrimitives';
import { TXN_FILTERS, selectCls } from '../walletPage.constants';
import { TransactionsTable } from './TransactionsTable';
import { SettlementsTable } from './SettlementsTable';
import { TransactionDetailModal } from './TransactionDetailModal';
import type { Settlement, Txn } from '../WalletPage.types';

type WalletTab = 'transactions' | 'withdrawals';

type TransactionsPanelProps = {
  tab: WalletTab;
  onTabChange: (tab: WalletTab) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  onExportCsv: () => void;
  isExporting: boolean;
  txnPage: number;
  onTxnPageChange: (page: number) => void;
  settPage: number;
  onSettPageChange: (page: number) => void;
  pageSize: number;
  isTxnsLoading: boolean;
  isTxnsFetching?: boolean;
  txns: Txn[];
  txnTotal: number;
  txnTotalPages: number;
  isSettlementsLoading: boolean;
  isSettlementsFetching?: boolean;
  settlements: Settlement[];
  settTotal: number;
  settlementTotalPages: number;
  invoiceLoadingId?: string;
  onDownloadTxnInvoice: (txn: Txn) => void;
  onViewSettlementInvoice: (settlement: Settlement) => void;
};

export function TransactionsPanel({
  tab,
  onTabChange,
  reason,
  onReasonChange,
  onExportCsv,
  isExporting,
  txnPage,
  onTxnPageChange,
  settPage,
  onSettPageChange,
  pageSize,
  isTxnsLoading,
  isTxnsFetching = false,
  txns,
  txnTotal,
  txnTotalPages,
  isSettlementsLoading,
  isSettlementsFetching = false,
  settlements,
  settTotal,
  settlementTotalPages,
  invoiceLoadingId,
  onDownloadTxnInvoice,
  onViewSettlementInvoice,
}: TransactionsPanelProps) {
  const [detailTxn, setDetailTxn] = useState<Txn | null>(null);

  // Clamp pages if API returns fewer pages than requested.
  useEffect(() => {
    if (txnPage > txnTotalPages) onTxnPageChange(txnTotalPages);
  }, [txnPage, txnTotalPages, onTxnPageChange]);

  useEffect(() => {
    if (settPage > settlementTotalPages) onSettPageChange(settlementTotalPages);
  }, [settPage, settlementTotalPages, onSettPageChange]);

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--dash-line)] bg-white">
      <div className="flex flex-col gap-3 border-b border-[var(--dash-line)] px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="flex gap-1 rounded-lg bg-[var(--dash-soft)] p-0.5">
          {(['transactions', 'withdrawals'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onTabChange(t)}
              className={`rounded-md px-3.5 py-1.5 text-sm font-medium capitalize transition ${
                tab === t
                  ? 'bg-white text-[var(--dash-ink)] shadow-sm'
                  : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {tab === 'transactions' ? (
            <select
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              className={selectCls}
            >
              {TXN_FILTERS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={onExportCsv}
            disabled={isExporting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--dash-line)] px-3 py-2 text-sm font-medium text-[var(--dash-ink)] transition hover:bg-[var(--dash-soft)] disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>

      {tab === 'transactions' ? (
        isTxnsLoading && txns.length === 0 ? (
          <Spinner />
        ) : txns.length === 0 ? (
          <EmptyMsg text="No transactions found." />
        ) : (
          <TransactionsTable
            txns={txns}
            page={txnPage}
            totalPages={txnTotalPages}
            total={txnTotal}
            pageSize={pageSize}
            isFetching={isTxnsFetching}
            onPageChange={onTxnPageChange}
            invoiceLoadingId={invoiceLoadingId}
            onViewDetails={setDetailTxn}
            onDownloadInvoice={onDownloadTxnInvoice}
          />
        )
      ) : isSettlementsLoading && settlements.length === 0 ? (
        <Spinner />
      ) : settlements.length === 0 ? (
        <EmptyMsg text="No withdrawal requests." />
      ) : (
        <SettlementsTable
          settlements={settlements}
          page={settPage}
          totalPages={settlementTotalPages}
          total={settTotal}
          pageSize={pageSize}
          isFetching={isSettlementsFetching}
          onPageChange={onSettPageChange}
          invoiceLoadingId={invoiceLoadingId}
          onViewInvoice={onViewSettlementInvoice}
        />
      )}

      <TransactionDetailModal
        open={Boolean(detailTxn)}
        txn={detailTxn}
        onClose={() => setDetailTxn(null)}
      />
    </div>
  );
}
